import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitInquiry } from "@/lib/api";
import { TEA_TYPE_OPTIONS, APPLICATION_OPTIONS, PACKAGING_OPTIONS, CONTACT_COUNTRIES } from "@/data/industries";

const INITIAL = {
  company: "",
  contact_name: "",
  email: "",
  country: "",
  website: "",
  quantity: "",
  tea_type: "",
  application: "",
  packaging: "",
  message: "",
  phone: "",
};

const TITLE_KEYS = {
  quote: "quote_dialog.title",
  sample: "quote_dialog.sample_title",
  catalogue: "quote_dialog.catalogue_title",
  meeting: "quote_dialog.meeting_title",
};

export const InquiryDialog = ({ trigger, type = "quote", presetTeaType = "" }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ ...INITIAL, tea_type: presetTeaType });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitInquiry({ ...form, inquiry_type: type });
      toast.success(t("contact.form.success_toast"));
      setForm({ ...INITIAL, tea_type: presetTeaType });
      setOpen(false);
    } catch (err) {
      toast.error(t("contact.form.error_toast"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild data-testid={`inquiry-trigger-${type}`}>
        {trigger}
      </DialogTrigger>
      <DialogContent
        data-testid="quote-request-form"
        className="max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl md:text-4xl font-light tracking-tight text-foreground">
            {t(TITLE_KEYS[type] || TITLE_KEYS.quote)}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            {t("quote_dialog.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("contact.form.company")}*</Label>
            <Input
              required
              value={form.company}
              onChange={set("company")}
              data-testid="input-company"
              className="mt-1 border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-forest-500 h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("contact.form.contact_name")}*</Label>
            <Input
              required
              value={form.contact_name}
              onChange={set("contact_name")}
              data-testid="input-contact-name"
              className="mt-1 border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-forest-500 h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("contact.form.email")}*</Label>
            <Input
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              data-testid="input-email"
              className="mt-1 border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-forest-500 h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("contact.form.country")}*</Label>
            <Select value={form.country} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
              <SelectTrigger data-testid="input-country" className="mt-1 border-0 border-b rounded-none px-0 focus:ring-0 h-11">
                <SelectValue placeholder={t("contact.form.select_country")} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {CONTACT_COUNTRIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("contact.form.website")}</Label>
            <Input
              value={form.website}
              onChange={set("website")}
              placeholder="https://"
              data-testid="input-website"
              className="mt-1 border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-forest-500 h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("contact.form.phone")}</Label>
            <Input
              value={form.phone}
              onChange={set("phone")}
              placeholder="+"
              data-testid="input-phone"
              className="mt-1 border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-forest-500 h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("contact.form.tea_type")}</Label>
            <Select value={form.tea_type} onValueChange={(v) => setForm((f) => ({ ...f, tea_type: v }))}>
              <SelectTrigger data-testid="input-tea-type" className="mt-1 border-0 border-b rounded-none px-0 focus:ring-0 h-11">
                <SelectValue placeholder={t("contact.form.select_tea_type")} />
              </SelectTrigger>
              <SelectContent>
                {TEA_TYPE_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("contact.form.quantity")}</Label>
            <Input
              value={form.quantity}
              onChange={set("quantity")}
              placeholder="e.g. 5,000 kg / 40 ft container"
              data-testid="input-quantity"
              className="mt-1 border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-forest-500 h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("contact.form.application")}</Label>
            <Select value={form.application} onValueChange={(v) => setForm((f) => ({ ...f, application: v }))}>
              <SelectTrigger data-testid="input-application" className="mt-1 border-0 border-b rounded-none px-0 focus:ring-0 h-11">
                <SelectValue placeholder={t("contact.form.select_application")} />
              </SelectTrigger>
              <SelectContent>
                {APPLICATION_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("contact.form.packaging")}</Label>
            <Select value={form.packaging} onValueChange={(v) => setForm((f) => ({ ...f, packaging: v }))}>
              <SelectTrigger data-testid="input-packaging" className="mt-1 border-0 border-b rounded-none px-0 focus:ring-0 h-11">
                <SelectValue placeholder={t("contact.form.select_packaging")} />
              </SelectTrigger>
              <SelectContent>
                {PACKAGING_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("contact.form.message")}</Label>
            <Textarea
              value={form.message}
              onChange={set("message")}
              rows={4}
              data-testid="input-message"
              className="mt-1 border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-forest-500 resize-none"
            />
          </div>

          <DialogFooter className="sm:col-span-2 pt-4">
            <Button
              type="submit"
              disabled={busy}
              data-testid="inquiry-submit"
              className="w-full sm:w-auto rounded-full bg-forest-600 hover:bg-forest-700 text-white h-12 px-8 font-normal tracking-wide"
            >
              {busy ? t("contact.form.sending") : t("cta.send")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InquiryDialog;
